import type { ForwardedRef, ReactNode } from 'react';
import type { GeneralMetaData } from 'apps/shared/data-access/types/metadata/metadata.types';
import type { PathRouteProps } from 'react-router';
import type { Pages } from 'apps/shared/data-access/types/routing.types';
import type { UserItemProps } from 'apps/shared/data-access/types/users/users.types';
import type { Props as StatusMessageProps } from 'components/modal/StatusMessage';
import type { GenericButtonTypes } from 'components/buttons/GenericButton';

export interface WrapperProps {
  // used for styled components transient props
  $mainContentWidth: number;
}

export interface AuthStatus {
  isAuthenticated: boolean;
  toAuthPath?: () => void;
  metadata?: GeneralMetaData;
  statusCode?: number;
  checkAuth?: boolean;
}

export type FontSize = {
  textXs: '0.75rem';
  textSm: '0.875rem';
  textBase: '0.938rem';
  textLg: '1.125rem';
  textXl: '1.25rem';
  text2xl: '1.563rem';
  text3xl: '1.875rem';
  text4xl: '2.25rem';
  text5xl: '3rem';
};

export enum FixedSizes {
  sidebarWidth = 255, // Must match the width specified an App.scss $SIDEBAR_WIDTH
  headerHeight = 65 - 1, // -1 to negate shadow/border
  pageNavMenu = 62.8,
  pageNavMenuButtons = 62,
  pageNavMenuMobile = 64 + pageNavMenuButtons,
  pageNavTabMenu = 42 - 1, // -1 to negate shadow/border
  footerHeight = 30,
  headerHeightMobile = 126,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  tableMenuHeaderHeight = 66,
  tableMenuHeaderHeightMobile = 192,
  tableMenuFooterHeight = 64,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  tableMenuFooterHeightMobile = 66,
  bannerHeight = 34,
}
export enum Breakpoints {
  mobile = 576,
  tablet = 768,
  mediumScreen = 990,
  largeScreen = 1280,
}

export enum Users {
  YOU = 'you',
  OTHERS = 'others',
  ALL = 'all',
}

export enum Locales {
  ALL = 'all',
  EN = 'en',
  EN_GB = 'en_GB',
  EN_US = 'en_US',
  SV_SE = 'sv_SE',
  DA_DK = 'da_DK',
  DE = 'de',
  DE_DE = 'de_DE',
  FI_FI = 'fi_FI',
  NO_NO = 'no_NO',
  PT_PT = 'pt_PT',
}

export enum DatePlaceholders {
  DATE = 'YYYY-MM-DD',
  DATE_TIME = 'YYYY-MM-DD HH:MM',
}

export interface ConfigurationField {
  name: string;
  title: string;
  required: boolean;
  level: ConfigFieldLevel;
  type: ConfigFieldType;
  multipleSelect: boolean;
  allowAddition: boolean;
  group: ConfigurationFieldGroup;
  description?: string;
  possibleValues?: string[];
  defaultValue?: string | boolean;
  format?: string;
  cdata?: boolean;
  validationType?: ConfigFieldValidationType;
}

export interface ConfigurationFieldGroup {
  id: number;
  name: string;
  title: string;
  showTitle: boolean;
}

export enum ConfigFieldLevel {
  ROOT = 'ROOT',
  ACCOUNT = 'ACCOUNT',
  BOTH = 'BOTH',
}

export enum ConfigFieldType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  TEXT = 'TEXT',
  BOOLEAN = 'BOOLEAN',
  LIST = 'LIST',
  COUNTRY = 'COUNTRY',
  CURRENCY = 'CURRENCY',
  STRING_MAP_1_1 = 'STRING_MAP_1_1',
  STRING_MAP_1_N = 'STRING_MAP_1_N',
  STRING_MAP_N_1 = 'STRING_MAP_N_1',
}

export enum ConfigFieldValidationType {
  NONE = 'NONE',
  URL = 'URL',
  UUID = 'UUID',
  EMAIL = 'EMAIL',
}

export interface DropdownOption {
  key: string | number;
  text: string;
  value: string | number;
  icon?: string;
  disabled?: boolean;
}

export enum UpdateStatuses {
  SUCCESS = 'success',
  FAILED = 'failed',
}

export interface ActiveModal {
  modalProps: Omit<GenericModalProps, 'onClose'>;
  componentProps: Omit<
    UserItemProps,
    'isMerchantAdmin' | 'isOperatorAdmin' | 'merchantOptions' | 'setSubmitHandler'
  >;
}

export interface WithChildren {
  children?: ReactNode | ReactNode[];
}

export type ComponentWithChildren<T extends WithChildren> = (props: T) => ReactNode | null;

export type FunctionalComponent<T = Record<string, unknown>> = (
  props: T,
  ref?: ForwardedRef<HTMLElement>,
) => ReactNode | null;

export interface PageProps extends PathRouteProps {
  viewId: Pages;
}

export type Nullable<T> = T | null;

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type DeepRequired<T> = Required<{
  [K in keyof T]: T[K] extends Required<T[K]> ? T[K] : DeepRequired<T[K]>;
}>;

export type DeepNullable<T> = {
  [K in keyof T]: DeepNullable<T[K]> | null;
};

export type Nillable<T> = T | null | undefined;

export type SidebarTypes = 'overlay' | 'uncover';

export const HistoryEventType = {
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  CREATE: 'CREATE',
};

export type FilterKeyMapper = (value: FilterValue) => FilterValue;

export type FilterValue = string | number | boolean | null | undefined;

export interface GenericModalProps extends WithChildren {
  title?: string | string[];
  size?: 'tiny' | 'small' | 'large';
  isSubmitDisabled?: boolean;
  isCancelDisabled?: boolean;
  isDeleteDisabled?: boolean;
  isLoading?: boolean;
  isDeleting?: boolean;
  handleSubmitClick?: () => void;
  statusMessage?: StatusMessageProps | null;
  submitButtonText?: string;
  cancelButtonText?: string;
  onClose: VoidFunction;
  helpSection?: ReactNode;
  showDeleteButton?: boolean;
  onDelete?: VoidFunction;
  closeOnDimmerClick?: boolean;
  description?: string | ReactNode;
  imageContent?: boolean;
  dataTestId?: string;
  cancelButtonType?: GenericButtonTypes;
}
